import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useShareCasoGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      casoId, 
      groupId 
    }: { 
      casoId: string; 
      groupId: string; 
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Verificar se já existe compartilhamento
      const { data: existing } = await supabase
        .from('caso_group_shares')
        .select('id')
        .eq('caso_id', casoId)
        .eq('group_id', groupId)
        .maybeSingle();

      if (existing) {
        throw new Error('Este caso já está compartilhado com este grupo');
      }

      // Inserir compartilhamento
      const { error } = await supabase
        .from('caso_group_shares')
        .insert({
          caso_id: casoId,
          group_id: groupId,
          shared_by_user_id: user.id,
          permission_level: 'view',
        });

      if (error) throw error;

      // Buscar dados do caso
      const { data: casoData } = await supabase
        .from('casos')
        .select('title')
        .eq('id', casoId)
        .single();

      // Buscar perfil do remetente
      const { data: senderProfile } = await supabase
        .from('users')
        .select('display_name')
        .eq('id', user.id)
        .single();

      const senderName = senderProfile?.display_name || 'Alguém';
      const casoTitle = casoData?.title || 'um caso';

      // Buscar todos os membros do grupo
      const { data: groupMembers } = await supabase
        .from('sharing_group_members')
        .select('user_id')
        .eq('group_id', groupId);

      if (groupMembers && groupMembers.length > 0) {
        // Criar notificações para cada membro
        const notificationInserts = groupMembers.map(member => ({
          type: 'caso_shared',
          title: 'Novo caso compartilhado via grupo',
          message: `${senderName} compartilhou "${casoTitle}" com seu grupo`,
          target_user_id: member.user_id,
          sent_by: user.id,
          metadata: { 
            caso_id: casoId,
            group_id: groupId,
            shared_via: 'group'
          },
        }));

        const { error: notifError } = await supabase
          .from('notifications')
          .insert(notificationInserts);

        if (notifError) {
          console.error('Erro ao criar notificações:', notifError);
        }

        // Registrar atividade
        await supabase.from('firm_activity_log').insert({
          firm_id: '00000000-0000-0000-0000-000000000000',
          actor_id: user.id,
          event_type: 'case_shared_group',
          resource_type: 'caso',
          resource_id: casoId,
          metadata: { 
            group_id: groupId,
            members_notified: groupMembers.length
          }
        });
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shared-users'] });
      queryClient.invalidateQueries({ queryKey: ['shared-caso-groups', variables.casoId] });
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      queryClient.invalidateQueries({ queryKey: ['global-share-count'] });
      toast.success('Caso compartilhado com o grupo!');
    },
    onError: (error: Error) => {
      if (error.message.includes('já está compartilhado')) {
        toast.error('Este caso já está compartilhado com este grupo');
      } else {
        toast.error('Erro ao compartilhar com o grupo');
      }
    },
  });
}
